import {
  BaseResource,
  NestedResourceNode,
  paginatedListSerializer,
  ResourceAction,
  ResourceActionV2,
  sortFilterSerializer,
  uuidObjectReferenceSerializer,
  UuidResourceParam,
} from "@legacy-megarax/rest-resource";
import { MSerializers } from "@legacy-megarax/serializers";

import { deliveryAddressSerializer, gtinSerializer } from "../common";
import {
  AddLineInput,
  createCopyInputSerializer,
  editCustomersInputSerializer,
  MyDraftListFilters,
  MyDraftSimple,
  MyDraftSimpleCustomer,
  myOrderSortFields,
  PlaceOrderInput,
  StartDraftInput,
  UpdateLineInput,
} from "./featureInterfaces";
import { OrderDraftDto, orderDraftDtoSerializer } from "./orderDraftDto";

const listAction = ResourceAction({
  name: "list",
  method: "get",
  path: "",
  querySerializer: MSerializers.object<MyDraftListFilters>({
    limit: MSerializers.integer.optional(),
    offset: MSerializers.integer.optional(),
    searchText: MSerializers.string.optional(),
    sortBy: sortFilterSerializer(...myOrderSortFields).optional(),
  }),
  responseSerializer: MSerializers.result(
    paginatedListSerializer(
      MSerializers.object<MyDraftSimple>({
        buyer: MSerializers.object<MyDraftSimpleCustomer>({
          name: MSerializers.string.optional(),
          uuid: MSerializers.uuid(),
        }),
        payer: MSerializers.object<MyDraftSimpleCustomer>({
          name: MSerializers.string.optional(),
          uuid: MSerializers.uuid(),
        }),
        draftStartedAt: MSerializers.datetime,
        isPlaced: MSerializers.boolean,
        placedAt: MSerializers.datetime.nullable(),
        uuid: MSerializers.uuid(),
      }),
    ),
  ),
});

const linesNestedResource = NestedResourceNode("lines", {
  byUuid: UuidResourceParam("lineUuid", {
    remove: ResourceActionV2({
      name: "removeLine",
      method: "delete",
      path: "",
      responseErrorSerializer: MSerializers.stringOneOf(
        "OrderNotFound",
        "DraftNotOpen",
        "UserNotOrderOwner",
        "LineDoesNotExist",
      ),
    }),
    update: ResourceActionV2({
      name: "updateLine",
      method: "put",
      path: "",
      requestBodySerializer: MSerializers.object<UpdateLineInput>({
        promoPrice: MSerializers.decimal.nullable(),
        quantity: MSerializers.integer,
      }),
      responseErrorSerializer: MSerializers.stringOneOf(
        "OrderNotFound",
        "DraftNotOpen",
        "UserNotOrderOwner",
        "LineDoesNotExist",
        "InvalidLineQuantity",
      ),
    }),
  }),
  add: ResourceActionV2({
    name: "addLine",
    method: "post",
    path: "",
    requestBodySerializer: MSerializers.object<AddLineInput>({
      lineUuid: MSerializers.uuid(),
      item: MSerializers.object({ gtin: gtinSerializer }),
      promoPrice: MSerializers.decimal.nullable(),
      quantity: MSerializers.integer,
    }),
    responseErrorSerializer: MSerializers.stringOneOf(
      "OrderNotFound",
      "DraftNotOpen",
      "UserNotOrderOwner",
      "LineAlreadyExists",
      "InvalidLineQuantity",
    ),
  }),
});

export const orderDraftResource = BaseResource(["sales", "orderDrafts"], {
  list: listAction,
  byUuid: UuidResourceParam("uuid", {
    retrieve: ResourceActionV2({
      path: "",
      name: "retrieve",
      responseValueSerializer: orderDraftDtoSerializer,
      responseErrorSerializer: MSerializers.stringOneOf(
        "DraftNotFound",
        "PriceListNotFound",
        "DraftDiscarded",
        "DraftFinalized",
      ),
    }),
    place: ResourceActionV2({
      name: "place",
      method: "post",
      requestBodySerializer: MSerializers.object<PlaceOrderInput>({
        deliveryAddress: deliveryAddressSerializer,
        comment: MSerializers.string,
        deliveryDeadline: MSerializers.dateOnly,
        externalOrderNumber: MSerializers.string,
      }),
      responseErrorSerializer: MSerializers.stringOneOf(
        "PriceListNotFound",
        "OrderNotFound",
        "DraftNotOpen",
        "ItemNotFound",
        "UserNotOrderOwner",
        "UnknownGtin",
      ),
    }),
    discard: ResourceActionV2({
      name: "discard",
      method: "delete",
      path: "",
      responseErrorSerializer: MSerializers.stringOneOf("OrderNotFound", "DraftNotOpen", "UserNotOrderOwner"),
    }),
    editCustomers: ResourceActionV2({
      name: "editCustomers",
      method: "put",
      path: "",
      requestBodySerializer: editCustomersInputSerializer,
      responseErrorSerializer: MSerializers.stringOneOf("OrderNotFound", "DraftNotOpen", "UserNotOrderOwner"),
    }),
    lines: linesNestedResource,
  }),
  startDraft: ResourceActionV2({
    path: "",
    name: "startDraft",
    method: "post",
    requestBodySerializer: MSerializers.object<StartDraftInput>({
      uuid: MSerializers.uuid(),
      payerDiscountRate: MSerializers.decimal,
      buyer: uuidObjectReferenceSerializer,
      payer: uuidObjectReferenceSerializer,
      recipient: uuidObjectReferenceSerializer,
      priceList: uuidObjectReferenceSerializer,
    }),
    responseErrorSerializer: MSerializers.stringOneOf("InvalidDiscountRate"),
  }),
  createCopy: ResourceActionV2({
    name: "createCopy",
    method: "post",
    path: "createCopy",
    responseErrorSerializer: MSerializers.stringOneOf("DraftNotFound"),
    requestBodySerializer: createCopyInputSerializer,
  }),
});
